(ns common.ui.validation.db
  (:require [clojure.spec.alpha :as spec]
            [common.datascript.schema :refer [owner-id-attr schema unique-within-container?]]
            [common.datascript.util :refer [transform-pull-tree]]
            [common.util :as util]
            [lemonpi.content-function.shared.validation :as v]
            [re-frame.core :as rf]
            [re-posh.core :as rp]
            [reagent.ratom :as ratom]))

(spec/def :validation/stages
  (spec/and set? (spec/coll-of v/error-stages)))

;; when implementing this function (defmethod), ensure that this expression is
;; evaluating its subscriptions (if any) in a non-lazy way due to use of
;; ratom/reaction later in the code.
(defmulti transform-entity-validate (fn [entity-type _e] entity-type))

(defmethod transform-entity-validate :default
  [_ e]
  e)

;; when implementing this function (defmethod), ensure that this expression is
;; evaluating its subscriptions (if any) in a non-lazy way due to use of
;; ratom/reaction later in the code.
(defmulti validation-schema
  (fn [schema _value] schema))

(defmethod validation-schema :default
  [schema value]
  {:validation/schema schema
   :validation/value value})

(defn validate [schema value]
  (sequence
   (map #(select-keys % [:code :stage :message]))
   (util/remove-duplicate-by
    :code
    (v/validate schema value))))

(rp/reg-query-sub :validation/other-values-globally
  '[:find [?v ...]
    :in $ ?e ?attr
    :where [?o ?attr ?v]
    [(not= ?e ?o)]])

(rp/reg-query-sub :validation/other-values-at-same-level
  '[:find [?v ...]
    :in $ ?owner-id-attr ?e ?attr
    :where
    [?e ?owner-id-attr ?en]
    [?o ?owner-id-attr ?en]
    [(not= ?e ?o)]
    [?o ?attr ?v]])

(rp/reg-query-sub :validation/all-values-at-same-level
  '[:find [?v ...]
    :in $ ?owner-id-attr ?en ?attr
    :where
    [?o ?owner-id-attr ?en]
    [?o ?attr ?v]])

(rf/reg-sub :validation/new-value-is-unique?
  (fn [[_ parent-eid eid attr _]]
    (let [unique-within-container? (unique-within-container? schema attr)]
      (rf/subscribe (cond
                      (not unique-within-container?)
                      [:validation/other-values-globally eid attr]

                      (and unique-within-container? (pos-int? eid))
                      [:validation/other-values-at-same-level (owner-id-attr attr) eid attr]

                      (and unique-within-container? (some? parent-eid))
                      [:validation/all-values-at-same-level (owner-id-attr attr) parent-eid attr]))))
  (fn [existing-values [_ _ _ _ new-value]]
    (not (contains? (set existing-values) new-value))))

(rf/reg-sub-raw
  ;; raw because transform-entity-validate/validation-schema implementations can
  ;; contain subscriptions. If there as any subs in transform-entity-validate or
  ;; validation-schema implementations, these should be non-lazy
  :validation/enrich-value
  (fn [_ [_ attr value]]
    (ratom/reaction
     (validation-schema attr (transform-pull-tree (memoize transform-entity-validate) value)))))

(rf/reg-sub-raw :validation/retrieve-and-enrich-value
  (fn [_ [_ eid attr pull-pattern]]
    (ratom/reaction
     @(rf/subscribe [:validation/enrich-value attr @(rf/subscribe [:ui.store/pull-entity pull-pattern eid])]))))

;; get the validation value from the argument itself
(rf/reg-sub :validation/results-from-value
  (fn [[_ attr value]]
    (rf/subscribe [:validation/enrich-value attr value]))
  (fn [{:validation/keys [schema value]} _]
    (validate schema value)))

;; get the validation value from the Datascript db
(rf/reg-sub :validation/results-from-db
  (fn [[_ eid attr pull-pattern]]
    (rf/subscribe [:validation/retrieve-and-enrich-value eid attr pull-pattern]))
  (fn [{:validation/keys [schema value]} _]
    (validate schema value)))

(rf/reg-sub :validation/results-filtered-by-stage
  (fn [[_ parent-eid eid attr value _]]
    [(rf/subscribe [:validation/results-from-value attr value])
     (rf/subscribe [:validation/new-value-is-unique? parent-eid eid attr value])])
  (fn [[validation-results can-rename-to-unique-value?] [_ _parent-eid _eid attr _value stages]]
    {:pre [(spec/assert :validation/stages stages)]}
    (not-empty
     (group-by :stage
               (cond-> (filter #(contains? stages (:stage %)) validation-results)
                 ;; if there are duplicated values, append error as if it were
                 ;; returned by the validation library
                 (and (not can-rename-to-unique-value?)
                      (v/should-be-unique? attr)
                      (contains? stages :save-blocking))
                 (conj {:code (keyword "unique" (name attr))
                        :stage :save-blocking
                        :message (v/duplicate-error-message attr)}))))))
